import React, { useState, useEffect, useRef } from 'react';
import { Download, Archive, ArchiveRestore, Brain, FileDown, Filter, Plus, ArrowLeft, TrendingUp, Clock, Target, Edit, MessageCircle, Send, Image as ImageIcon, Trash2, RefreshCw, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

const DecisionTracker = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalDecisions: 0,
    decisionVolumeTrend: 'N/A',
    statusBreakdown: { pending: 0, implemented: 0, completed: 0, overdue: 0 },
    collaborationScore: 0,
    momentumIndicator: 'Neutral',
    hotspotFocus: 'N/A',
    topPerformer: 'No data',
    wasteReduction: 0,
    overdueCount: 0,
    pendingCount: 0,
    isTeamView: false
  });
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterObjective, setFilterObjective] = useState('all');
  const [viewMode, setViewMode] = useState('active');
  const [loading, setLoading] = useState(false);
  const [showNewDecisionForm, setShowNewDecisionForm] = useState(false);
  const [showEditDecisionForm, setShowEditDecisionForm] = useState(null);
  const [showImplementModal, setShowImplementModal] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(null);
  const [showAIInsights, setShowAIInsights] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(null);
  const [showDecisionAssistant, setShowDecisionAssistant] = useState(null);
  const [aiInsights, setAiInsights] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedDecisions, setSelectedDecisions] = useState(new Set());
  const [error, setError] = useState(null);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const exportTemplateRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [decisionsPerPage] = useState(10);

  const objectiveTypes = [
    { value: 'conversion', label: 'Conversion', icon: '💰' },
    { value: 'brand_awareness', label: 'Brand Awareness', icon: '📢' },
    { value: 'engagement', label: 'Engagement', icon: '❤️' },
    { value: 'traffic', label: 'Traffic', icon: '🚗' },
    { value: 'retention', label: 'Retention', icon: '🔄' },
    { value: 'email', label: 'Email Marketing', icon: '📧' },
    { value: 'lead_gen', label: 'Lead Generation', icon: '🎯' },
    { value: 'custom', label: 'Custom (specify below)', icon: '⚡' }
  ];

  const objectiveMetrics = {
    conversion: {
      primary: { label: 'Conversion Rate Change', unit: '%', icon: '💰' },
      secondary: { label: 'Cost Per Conversion', unit: '$', icon: '💵' },
      tertiary: { label: 'ROAS Change', unit: '%', icon: '📈' }
    },
    brand_awareness: {
      primary: { label: 'Reach Change', unit: '%', icon: '👥' },
      secondary: { label: 'Impressions Change', unit: '%', icon: '👁️' },
      tertiary: { label: 'Brand Lift', unit: '%', icon: '📊' }
    },
    engagement: {
      primary: { label: 'Engagement Rate', unit: '%', icon: '❤️' },
      secondary: { label: 'Comments/Shares', unit: '%', icon: '💬' },
      tertiary: { label: 'Cost Per Engagement', unit: '$', icon: '💸' }
    },
    traffic: {
      primary: { label: 'Traffic Change', unit: '%', icon: '🚗' },
      secondary: { label: 'CTR Change', unit: '%', icon: '🖱️' },
      tertiary: { label: 'Cost Per Click', unit: '$', icon: '💰' }
    },
    retention: {
      primary: { label: 'Retention Rate', unit: '%', icon: '🔄' },
      secondary: { label: 'Churn Reduction', unit: '%', icon: '📉' },
      tertiary: { label: 'LTV Change', unit: '%', icon: '💎' }
    },
    email: {
      primary: { label: 'Open Rate Change', unit: '%', icon: '📧' },
      secondary: { label: 'Click Rate Change', unit: '%', icon: '🖱️' },
      tertiary: { label: 'Unsubscribe Rate', unit: '%', icon: '🚫' }
    },
    lead_gen: {
      primary: { label: 'Lead Volume Change', unit: '%', icon: '🎯' },
      secondary: { label: 'Cost Per Lead', unit: '$', icon: '💵' },
      tertiary: { label: 'Lead Quality Score', unit: '/10', icon: '⭐' }
    },
    custom: {
      primary: { label: 'Primary Metric', unit: '%', icon: '📊' },
      secondary: { label: 'Secondary Metric', unit: '%', icon: '📈' },
      tertiary: { label: 'Tertiary Metric', unit: '%', icon: '📉' }
    }
  };

  const platformOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'google', label: 'Google' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'email', label: 'Email' },
    { value: 'crm', label: 'CRM' },
    { value: 'other', label: 'Other (specify below)' }
  ];

  const calculateMetrics = async (decisions) => {
    try {
      if (!decisions || decisions.length === 0) {
        return {
          totalDecisions: 0,
          decisionVolumeTrend: 'N/A',
          statusBreakdown: { pending: 0, implemented: 0, completed: 0, overdue: 0 },
          activityScore: 0,
          executionRate: 0,
          momentumIndicator: 'Neutral',
          hotspotFocus: 'N/A',
          topObjective: 'No data',
          wasteReduction: 0,
          overdueCount: 0,
          pendingCount: 0
        };
      }

      const totalDecisions = decisions.length;
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const statusBreakdown = {
        pending: decisions.filter(d => d.status === 'pending' || d.status === 'pending_confirmation').length,
        implemented: decisions.filter(d => d.status === 'implemented').length,
        completed: decisions.filter(d => d.status === 'completed').length,
        overdue: decisions.filter(d => d.status === 'overdue').length
      };

      const implementedDecisions = decisions.filter(d => d.implemented_at && d.decided_at);
      const avgDaysToImplement = implementedDecisions.length > 0 
        ? implementedDecisions
            .map(d => (new Date(d.implemented_at) - new Date(d.decided_at)) / (1000 * 60 * 60 * 24))
            .reduce((sum, days) => sum + days, 0) / implementedDecisions.length
        : 0;

      const decisionVolumeTrend = avgDaysToImplement === 0 ? 'No Data' : 
        avgDaysToImplement < 1 ? 'Lightning' :
        avgDaysToImplement < 3 ? 'Fast' :
        avgDaysToImplement < 7 ? 'Steady' : 'Slow';

      const decisionsThisWeek = decisions.filter(d => new Date(d.decided_at) > oneWeekAgo).length;
      const activityScore = totalDecisions > 0
        ? Math.min((totalDecisions / 10) * 20 + (decisionsThisWeek / totalDecisions) * 80, 100)
        : 0;

      const executedDecisions = statusBreakdown.implemented + statusBreakdown.completed;
      const executionRate = totalDecisions > 0 ? (executedDecisions / totalDecisions) * 100 : 0;

      const recentActivity = decisions.filter(d => new Date(d.decided_at) > oneWeekAgo).length;
      const momentumIndicator = recentActivity > (totalDecisions * 0.1) ? 'Accelerating' : recentActivity > 0 ? 'Building' : 'Stagnant';

      const objectives = decisions.map(d => d.objective_type).filter(o => o);
      const objectiveCount = {};
      objectives.forEach(obj => {
        objectiveCount[obj] = (objectiveCount[obj] || 0) + 1;
      });
      const topObjective = Object.keys(objectiveCount).length > 0
        ? Object.entries(objectiveCount).sort((a, b) => b[1] - a[1])[0][0]
        : 'No data';

      const platforms = decisions.map(d => d.displayPlatform || d.platform).filter(p => p);
      const platformCount = {};
      platforms.forEach(plat => {
        platformCount[plat] = (platformCount[plat] || 0) + 1;
      });
      const hotspotFocus = Object.keys(platformCount).length > 0
        ? Object.entries(platformCount).sort((a, b) => b[1] - a[1])[0][0]
        : 'N/A';

      let totalCostSavings = 0;
      decisions.forEach(d => {
        if (d.actual_impact) {
          const costImpact = parseFloat(d.actual_impact.cost_impact || 0);
          if (costImpact < 0) totalCostSavings += Math.abs(costImpact);
        }
      });

      return {
        totalDecisions,
        decisionVolumeTrend,
        statusBreakdown,
        activityScore: parseFloat(activityScore.toFixed(1)),
        executionRate: parseFloat(executionRate.toFixed(1)),
        momentumIndicator,
        hotspotFocus,
        topObjective,
        wasteReduction: parseFloat(totalCostSavings.toFixed(1)),
        overdueCount: statusBreakdown.overdue,
        pendingCount: statusBreakdown.pending
      };
    } catch (error) {
      logger.error('Metrics calculation error:', error);
      return {
        totalDecisions: 0,
        decisionVolumeTrend: 'N/A',
        statusBreakdown: { pending: 0, implemented: 0, completed: 0, overdue: 0 },
        activityScore: 0,
        executionRate: 0,
        momentumIndicator: 'Neutral',
        hotspotFocus: 'N/A',
        topObjective: 'No data',
        wasteReduction: 0,
        overdueCount: 0,
        pendingCount: 0
      };
    }
  };

  const fetchDecisions = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        setDecisions([]);
        setAnalytics({
          totalDecisions: 0,
          decisionVolumeTrend: 'N/A',
          statusBreakdown: { pending: 0, implemented: 0, completed: 0, overdue: 0 },
          collaborationScore: 0,
          momentumIndicator: 'Neutral',
          hotspotFocus: 'N/A',
          topPerformer: 'No data',
          wasteReduction: 0,
          overdueCount: 0,
          pendingCount: 0,
          isTeamView: false
        });
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterObjective !== 'all') params.append('objective', filterObjective);
      params.append('archived', viewMode === 'archived');

      const response = await fetch(`${BACKEND_URL}/api/decisions?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setError('Authentication failed. Please log in again.');
        setDecisions([]);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const decisions = data.decisions || [];
      
      const processedDecisions = decisions.map(decision => ({
        ...decision,
        displayPlatform: decision.platform === 'other' && decision.custom_platform 
          ? decision.custom_platform 
          : decision.platform
      }));
      
      setDecisions(processedDecisions);

      const calculatedMetrics = await calculateMetrics(processedDecisions);
      setAnalytics({
        ...calculatedMetrics,
        collaborationScore: calculatedMetrics.activityScore,
        topPerformer: 'Individual Mode',
        isTeamView: false
      });
    } catch (error) {
      logger.error('Error fetching decisions:', error);
      setError('Failed to load decisions. Please try again.');
      setDecisions([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'border-green-500 text-green-400',
      implemented: 'border-emerald-500 text-emerald-400',
      confirmed: 'border-cyan-500 text-cyan-400',
      pending: 'border-yellow-500 text-yellow-400',
      pending_confirmation: 'border-orange-500 text-orange-400',
      overdue: 'border-red-500 text-red-400'
    };
    return colors[status] || 'border-gray-500 text-gray-400';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: '✅',
      implemented: '🚀',
      confirmed: '✓',
      pending: '⏳',
      pending_confirmation: '🤖',
      overdue: '🔥'
    };
    return icons[status] || '❓';
  };

  const getObjectiveIcon = (objective) => {
    return objectiveTypes.find(o => o.value === objective)?.icon || '⚡';
  };

  const handleArchive = async (decision, reason) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Failed to archive decision');
      }

      setDecisions(prev => prev.filter(d => d.id !== decision.id));
      setShowArchiveModal(null);
      setSelectedDecisions(prev => {
        const newSet = new Set(prev);
        newSet.delete(decision.id);
        return newSet;
      });
    } catch (error) {
      logger.error('Error archiving decision:', error);
      alert('Failed to archive decision. Please try again.');
    }
  };

  const handleUnarchive = async (decision) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/unarchive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to unarchive decision');
      }

      await fetchDecisions();
    } catch (error) {
      logger.error('Error unarchiving decision:', error);
      alert('Failed to restore decision. Please try again.');
    }
  };

  const generateAIInsights = async (decision) => {
    setAiLoading(true);
    setAiInsights('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/generate-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate insights');
      }

      const data = await response.json();
      setAiInsights(data.insights || 'No insights available');
    } catch (error) {
      logger.error('Error generating AI insights:', error);
      setAiInsights('Sorry, having trouble generating insights right now. Try again?');
    } finally {
      setAiLoading(false);
    }
  };

  const exportToImage = async () => {
    try {
      setExportingImage(true);
      
      if (!exportTemplateRef.current) {
        alert('Export template not found');
        return;
      }

      const canvas = await html2canvas(exportTemplateRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `marketing_decisions_${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowExportPreview(false);
      setSelectedDecisions(new Set());
      setShowExportMenu(false);
    } catch (error) {
      logger.error('Error exporting image:', error);
      alert(`Failed to export image: ${error.message}`);
    } finally {
      setExportingImage(false);
    }
  };

  const downloadAIInsights = () => {
    if (!aiInsights || !showAIInsights) return;
    
    const content = `Decision: ${showAIInsights.decision_title}\n\nAI Insights:\n${aiInsights}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai_insights_${showAIInsights.id}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const toggleDecisionSelection = (decisionId) => {
    setSelectedDecisions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(decisionId)) {
        newSet.delete(decisionId);
      } else {
        newSet.add(decisionId);
      }
      return newSet;
    });
  };

  const selectAllDecisions = () => {
    if (selectedDecisions.size === decisions.length) {
      setSelectedDecisions(new Set());
    } else {
      setSelectedDecisions(new Set(decisions.map(d => d.id)));
    }
  };

  const getActualImpactDisplay = (decision) => {
    if (!decision.actual_impact) return null;

    if (decision.actual_impact.metrics) {
      const metrics = decision.actual_impact.metrics;
      return (
        <div className="bg-white/5 rounded-lg p-3 mb-3 grid grid-cols-2 gap-2 text-xs">
          <div className="flex justify-between">
            <span>{metrics.primary.label}:</span>
            <span className={`font-bold ${metrics.primary.value > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {metrics.primary.value > 0 ? '+' : ''}{metrics.primary.value}{metrics.primary.unit}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Cost Impact:</span>
            <span className={`font-bold ${decision.actual_impact.cost_impact > 0 ? 'text-red-400' : 'text-green-400'}`}>
              ${Math.abs(decision.actual_impact.cost_impact)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Add this helper function after your existing functions (before the return statement)
const getPaginatedDecisions = () => {
  const startIndex = (currentPage - 1) * decisionsPerPage;
  const endIndex = startIndex + decisionsPerPage;
  return decisions.slice(startIndex, endIndex);
};

const totalPages = Math.ceil(decisions.length / decisionsPerPage);

// Reset to page 1 when filters change - add this useEffect
useEffect(() => {
  setCurrentPage(1);
}, [filterStatus, filterObjective, viewMode]);

// ============================================
// PAGINATION COMPONENT - Add this before the export
// ============================================
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  
  return (
    <div className="flex items-center justify-center gap-4 mt-8 mb-4">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
          currentPage === 1
            ? 'bg-white/5 text-gray-500 cursor-not-allowed'
            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10 hover:border-white/30'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Previous
      </button>
      
      <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl">
        <span className="text-white font-bold">{currentPage}</span>
        <span className="text-gray-400">of</span>
        <span className="text-white font-bold">{totalPages}</span>
      </div>
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
          currentPage === totalPages
            ? 'bg-white/5 text-gray-500 cursor-not-allowed'
            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10 hover:border-white/30'
        }`}
      >
        Next
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      if (authLoading) return;
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          fetchDecisions();
        } else {
          navigate('/login');
        }
      } catch (error) {
        logger.error('Auth check failed:', error);
        navigate('/login');
      }
    };

    checkAuthAndFetch();
  }, [authLoading, navigate]);

  useEffect(() => {
    if (!authLoading) {
      fetchDecisions();
    }
  }, [filterStatus, filterObjective, viewMode, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-800 flex items-center justify-center">
        <div className="text-gray-300 text-lg">Loading decisions...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const getFilteredDecisions = () => {
    if (selectedDecisions.size === 0) return decisions;
    return decisions.filter(d => selectedDecisions.has(d.id));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-800 text-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-start mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-red-900/50 border border-red-500 rounded-lg px-3 py-2 text-red-400 text-sm font-medium hover:bg-red-900/70 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </button>
          </div>
          
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">👑 Decision Tracker</h1>
            <p className="text-gray-400 text-sm">Marketing accountability made clear</p>
          </div>
          
          {/* Analytics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-red-400 mb-1">{analytics.totalDecisions || 0}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Total Decisions</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-red-400 mb-1">{analytics.activityScore || analytics.collaborationScore || 0}%</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Activity Score</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-red-400 mb-1">{analytics.executionRate || 0}%</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Execution Rate</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-red-400 mb-1">{analytics.decisionVolumeTrend || 'N/A'}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Velocity</div>
            </div>
          </div>
        </div>

        {/* View Toggle & Export */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'active'
                  ? 'bg-red-500 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Active Decisions
            </button>
            <button
              onClick={() => setViewMode('archived')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'archived'
                  ? 'bg-red-500 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <Archive className="w-4 h-4" />
              Archived
            </button>
          </div>
          
          <div className="relative">
            <button
              onClick={() => {
                if (selectedDecisions.size > 0) {
                  setShowExportPreview(true);
                } else {
                  setShowExportMenu(!showExportMenu);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <ImageIcon className="w-4 h-4" />
              {selectedDecisions.size > 0 ? `Export Selected (${selectedDecisions.size})` : 'Export as Image'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-3">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Filter by Status:</label>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'pending', 'implemented', 'completed', 'overdue'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    filterStatus === status
                      ? 'bg-red-500 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* New Decision Button */}
        {viewMode === 'active' && (
          <div className="mb-6">
            <button 
              onClick={() => setShowNewDecisionForm(true)}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium w-full max-w-md mx-auto block transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Log New Decision
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 text-red-400 text-center">
            {error}
          </div>
        )}

        {/* Select All */}
        {decisions.length > 0 && showExportMenu && (
          <div className="mb-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedDecisions.size === decisions.length}
              onChange={selectAllDecisions}
              className="mr-2"
            />
            <span className="text-sm text-gray-400">Select All</span>
          </div>
        )}

        {/* Decisions List */}
        <div className="space-y-4">
          {getPaginatedDecisions().map(decision => (
            <div 
              key={decision.id}
              className={`bg-white/5 border-2 rounded-xl p-4 cursor-pointer transition-all hover:bg-white/10 ${getStatusColor(decision.status)} flex items-start`}
              onClick={() => setSelectedDecision(decision)}
            >
    
              {showExportMenu && (
                <input
                  type="checkbox"
                  checked={selectedDecisions.has(decision.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleDecisionSelection(decision.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-2 mt-1"
                />
              )}
              <div className="flex-1">
                <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getStatusIcon(decision.status)}</span>
                    <span className="text-xs font-bold uppercase tracking-wide">
                      {decision.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getObjectiveIcon(decision.objective_type)}</span>
                    <span className="text-xs bg-white/10 px-2 py-1 rounded-full">
                      {decision.objective_type === 'custom' && decision.custom_objective 
                        ? decision.custom_objective 
                        : objectiveTypes.find(o => o.value === decision.objective_type)?.label}
                    </span>
                    {decision.accountability_score && (
                      <div className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-lg text-xs font-bold">
                        {decision.accountability_score}/10
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-white font-medium mb-2 italic">
                  "{decision.decision_title}"
                </div>
                
                <div className="text-sm text-gray-300 mb-2">
                  <strong className="text-yellow-400">{decision.decided_by}</strong>
                  {decision.displayPlatform && <span className="text-gray-400"> • {decision.displayPlatform}</span>}
                </div>

                <div className="text-xs text-gray-400 mb-3">
                  📅 {new Date(decision.decided_at).toLocaleDateString()}
                  {decision.status === 'pending' && (
                    <span className="text-yellow-400 font-medium"> • Ready to implement</span>
                  )}
                </div>

                {getActualImpactDisplay(decision)}

                <div className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded inline-block mb-3">
                  {decision.campaign_id}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {decision.status === 'pending' && (
                    <>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowImplementModal(decision);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                      >
                        ✅ Mark Implemented
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowEditDecisionForm(decision);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>
                    </>
                  )}
                  {decision.status === 'implemented' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCompleteModal(decision);
                      }}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      🎯 Mark Complete
                    </button>
                  )}
                  {decision.status === 'overdue' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImplementModal(decision);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      🔥 Implement Now
                    </button>
                  )}
                  
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDecisionAssistant(decision);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                  >
                    <MessageCircle className="w-3 h-3" />
                    Chat
                  </button>
                  
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAIInsights(decision);
                      generateAIInsights(decision);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                  >
                    <Brain className="w-3 h-3" />
                    Insights
                  </button>
                  
                  {viewMode === 'active' ? (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowArchiveModal(decision);
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      <Archive className="w-3 h-3" />
                      Archive
                    </button>
                  ) : (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnarchive(decision);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      <ArchiveRestore className="w-3 h-3" />
                      Restore
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

       {/* Pagination */}
<Pagination 
  currentPage={currentPage}
  totalPages={totalPages}
  onPageChange={setCurrentPage}
/>

{/* Empty State - update to check full decisions array */}
{decisions.length === 0 && !loading && (
  <div className="text-center py-12 text-gray-400">
    <div className="text-4xl mb-4">📋</div>
    <p>No decisions found for the selected filters.</p>
  </div>
)}

        {/* Modals */}
        {selectedDecision && (
          <DecisionModal 
            decision={selectedDecision}
            onClose={() => setSelectedDecision(null)}
            objectiveMetrics={objectiveMetrics}
          />
        )}

        {showAIInsights && (
          <AIInsightsModal 
            decision={showAIInsights}
            insights={aiInsights}
            loading={aiLoading}
            onClose={() => setShowAIInsights(null)}
            onDownload={downloadAIInsights}
            onRefresh={() => generateAIInsights(showAIInsights)}
          />
        )}

        {showArchiveModal && (
          <ArchiveModal 
            decision={showArchiveModal}
            onClose={() => setShowArchiveModal(null)}
            onArchive={handleArchive}
          />
        )}

        {showNewDecisionForm && (
          <NewDecisionForm 
            objectiveTypes={objectiveTypes}
            platformOptions={platformOptions}
            onClose={() => setShowNewDecisionForm(false)}
            onSubmit={() => {
              setShowNewDecisionForm(false);
              fetchDecisions();
            }}
          />
        )}

        {showEditDecisionForm && (
          <EditDecisionForm
            decision={showEditDecisionForm}
            objectiveTypes={objectiveTypes}
            platformOptions={platformOptions}
            onClose={() => setShowEditDecisionForm(null)}
            onSubmit={() => {
              setShowEditDecisionForm(null);
              fetchDecisions();
            }}
          />
        )}

        {showImplementModal && (
          <ImplementModal 
            decision={showImplementModal}
            objectiveMetrics={objectiveMetrics}
            onClose={() => setShowImplementModal(null)}
            onUpdate={() => {
              setShowImplementModal(null);
              fetchDecisions();
            }}
          />
        )}

        {showCompleteModal && (
          <CompleteModal 
            decision={showCompleteModal}
            onClose={() => setShowCompleteModal(null)}
            onUpdate={() => {
              setShowCompleteModal(null);
              fetchDecisions();
            }}
          />
        )}

        {showDecisionAssistant && (
          <DecisionAssistant
            decision={showDecisionAssistant}
            onClose={() => setShowDecisionAssistant(null)}
          />
        )}

       {showExportPreview && (() => {
          const filteredDecisions = getFilteredDecisions();
          
          const filteredAnalytics = {
            totalDecisions: filteredDecisions.length,
            statusBreakdown: {
              pending: filteredDecisions.filter(d => d.status === 'pending' || d.status === 'pending_confirmation').length,
              implemented: filteredDecisions.filter(d => d.status === 'implemented').length,
              completed: filteredDecisions.filter(d => d.status === 'completed').length,
              overdue: filteredDecisions.filter(d => d.status === 'overdue').length
            },
            executionRate: filteredDecisions.length > 0 
              ? parseFloat(((filteredDecisions.filter(d => d.status === 'implemented' || d.status === 'completed').length / filteredDecisions.length) * 100).toFixed(1))
              : 0,
            decisionVolumeTrend: (() => {
              const implementedDecisions = filteredDecisions.filter(d => d.implemented_at && d.decided_at);
              if (implementedDecisions.length === 0) return 'No Data';
              const avgDays = implementedDecisions
                .map(d => (new Date(d.implemented_at) - new Date(d.decided_at)) / (1000 * 60 * 60 * 24))
                .reduce((sum, days) => sum + days, 0) / implementedDecisions.length;
              return avgDays < 1 ? 'Lightning' : avgDays < 3 ? 'Fast' : avgDays < 7 ? 'Steady' : 'Slow';
            })(),
            wasteReduction: parseFloat(
              filteredDecisions.reduce((total, d) => {
                if (d.actual_impact) {
                  const costImpact = parseFloat(d.actual_impact.cost_impact || 0);
                  if (costImpact < 0) return total + Math.abs(costImpact);
                }
                return total;
              }, 0).toFixed(1)
            )
          };
          
          return (
            <ExportPreviewModal 
              decisions={filteredDecisions}
              analytics={filteredAnalytics}
              onClose={() => setShowExportPreview(false)}
              onExport={exportToImage}
              exportingImage={exportingImage}
              exportTemplateRef={exportTemplateRef}
            />
          );
        })()}
      </div>
    </div>
  );
};

// ============================================
// AI INSIGHTS MODAL - Updated for shorter content
// ============================================
const AIInsightsModal = ({ decision, insights, loading, onClose, onDownload, onRefresh }) => {
  // Split insights into paragraphs for better rendering
  const renderInsights = (text) => {
    if (!text) return null;
    
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    
    return paragraphs.map((paragraph, idx) => (
      <p key={idx} className="text-gray-200 leading-relaxed mb-4 last:mb-0">
        {paragraph.trim()}
      </p>
    ));
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-gray-700 flex justify-between items-start">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400 flex-shrink-0" />
              Quick Take
            </h2>
            <p className="text-sm text-gray-400 mt-1 truncate">
              {decision.decision_title}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {insights && !loading && (
              <>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onRefresh();
                  }}
                  className="text-gray-400 hover:text-purple-400 p-2 hover:bg-white/5 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload();
                  }}
                  className="text-gray-400 hover:text-purple-400 p-2 hover:bg-white/5 rounded-lg transition-colors"
                  title="Download"
                >
                  <FileDown className="w-4 h-4" />
                </button>
              </>
            )}
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-white p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Thinking...</p>
            </div>
          ) : (
            <div className="text-base">
              {renderInsights(insights)}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// DECISION ASSISTANT - Complete Rewrite
// ============================================
const DecisionAssistant = ({ decision, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);

  // Load conversation history on mount
  useEffect(() => {
    loadConversationHistory();
  }, [decision.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversationHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/conversation`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          // Convert DB format to chat format
          const formattedMessages = data.messages.map(msg => ({
            role: msg.role,
            content: msg.message
          }));
          setMessages(formattedMessages);
        } else {
          // No history - show welcome message
          setMessages([{
            role: 'assistant',
            content: getWelcomeMessage(decision)
          }]);
        }
      }
    } catch (error) {
      logger.error('Error loading conversation:', error);
      setMessages([{
        role: 'assistant',
        content: getWelcomeMessage(decision)
      }]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getWelcomeMessage = (decision) => {
    const hasResults = decision.actual_impact && Object.keys(decision.actual_impact).length > 0;
    
    if (decision.status === 'pending' || decision.status === 'pending_confirmation') {
      return `Ready to talk through "${decision.decision_title}"? What's on your mind?`;
    }
    if (decision.status === 'implemented' && hasResults) {
      return `I see you've got some results on "${decision.decision_title}". Want to dig into them?`;
    }
    if (decision.status === 'implemented') {
      return `"${decision.decision_title}" is live. How's it going so far?`;
    }
    if (decision.status === 'completed') {
      return `"${decision.decision_title}" is wrapped up. Want to do a quick post-mortem?`;
    }
    if (decision.status === 'overdue') {
      return `"${decision.decision_title}" has been sitting for a while. Still planning to do this?`;
    }
    return `Let's chat about "${decision.decision_title}". What do you need?`;
  };

  const clearConversation = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/conversation`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      setMessages([{
        role: 'assistant',
        content: getWelcomeMessage(decision)
      }]);
    } catch (error) {
      logger.error('Error clearing conversation:', error);
    }
  };

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    setInput('');
    setLoading(true);

    // Add user message immediately
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        suggestions: data.suggestions || []
      }]);

    } catch (error) {
      logger.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, something went wrong. Try again?"
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick suggestions based on decision state
  const getQuickStarters = () => {
    const hasResults = decision.actual_impact && Object.keys(decision.actual_impact).length > 0;
    
    if (decision.status === 'pending') {
      return [
        "What could go wrong?",
        "Give me a quick plan",
        "Is this worth doing?"
      ];
    }
    if (decision.status === 'implemented' && hasResults) {
      return [
        "Should I scale this?",
        "What's the verdict?",
        "Any red flags?"
      ];
    }
    if (decision.status === 'completed') {
      return [
        "What's the takeaway?",
        "Did it work?",
        "Would you do this again?"
      ];
    }
    if (decision.status === 'overdue') {
      return [
        "Should I just drop this?",
        "Make it simpler",
        "Is it still relevant?"
      ];
    }
    return [
      "What do you think?",
      "Help me out",
      "Quick advice?"
    ];
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
              <span className="truncate">Chat</span>
            </h3>
            <p className="text-xs text-gray-400 truncate mt-0.5">{decision.decision_title}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={clearConversation}
              className="text-gray-400 hover:text-red-400 p-2 hover:bg-red-900/20 rounded-lg transition-colors"
              title="Clear chat"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-white text-xl hover:bg-white/10 rounded-lg w-8 h-8 flex items-center justify-center"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadingHistory ? (
            <div className="text-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-500 text-sm">Loading chat...</p>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user' 
                      ? 'bg-purple-600 text-white rounded-br-md' 
                      : 'bg-gray-700 text-gray-100 rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                    
                    {/* Suggestions */}
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-600/50 flex flex-wrap gap-2">
                        {msg.suggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => sendMessage(suggestion)}
                            className="text-xs px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick starters - only show at beginning */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 justify-center pt-4">
                  {getQuickStarters().map((starter, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(starter)}
                      className="text-sm px-4 py-2 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 rounded-full transition-colors border border-purple-500/30"
                    >
                      {starter}
                    </button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="flex-1 bg-gray-700 border border-gray-600 rounded-full px-4 py-2.5 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-purple-500 transition-colors"
              disabled={loading || loadingHistory}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading || loadingHistory}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white p-2.5 rounded-full transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// REMAINING COMPONENTS (unchanged but included for completeness)
// ============================================

// Decision Modal Component
const DecisionModal = ({ decision, onClose, objectiveMetrics }) => {
  const getStatusStyle = (status) => {
    const styles = {
      completed: { color: 'text-green-400', bg: 'bg-green-500/20', icon: '✅' },
      implemented: { color: 'text-emerald-400', bg: 'bg-emerald-500/20', icon: '🚀' },
      confirmed: { color: 'text-cyan-400', bg: 'bg-cyan-500/20', icon: '✓' },
      pending: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: '⏳' },
      pending_confirmation: { color: 'text-orange-400', bg: 'bg-orange-500/20', icon: '🤖' },
      overdue: { color: 'text-red-400', bg: 'bg-red-500/20', icon: '🔥' }
    };
    return styles[status] || { color: 'text-gray-400', bg: 'bg-gray-500/20', icon: '❓' };
  };

  const statusStyle = getStatusStyle(decision.status);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-2">Decision Details</h2>
            <div className="flex items-center gap-3">
              <span className={`${statusStyle.bg} ${statusStyle.color} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
                <span>{statusStyle.icon}</span>
                <span className="uppercase">{decision.status.replace('_', ' ')}</span>
              </span>
              {decision.accountability_score && (
                <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-medium">
                  Score: {decision.accountability_score}/10
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl hover:bg-white/10 rounded-lg w-8 h-8 flex items-center justify-center transition-all">
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Campaign Info */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <h3 className="font-medium text-blue-400 text-sm uppercase tracking-wide mb-2">Campaign</h3>
              <p className="text-white font-mono text-sm">{decision.campaign_id}</p>
            </div>

            {/* Main Info Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Decision Title</h3>
                  <p className="text-white text-lg font-medium">"{decision.decision_title}"</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Description</h3>
                  <p className="text-gray-200">{decision.decision_description || 'No description provided'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Expected Outcome</h3>
                  <p className="text-yellow-400">{decision.expected_outcome || 'Not specified'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Owner</h3>
                  <p className="text-white">{decision.decided_by}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Platform</h3>
                  <p className="text-white">{decision.displayPlatform || decision.platform || 'Not specified'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-2">Objective</h3>
                  <p className="text-white">
                    {decision.objective_type === 'custom' && decision.custom_objective 
                      ? decision.custom_objective 
                      : decision.objective_type}
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-gray-900/50 rounded-lg p-4">
              <h3 className="font-medium text-gray-400 text-sm uppercase tracking-wide mb-3">Timeline</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-500 text-xs uppercase">Created</p>
                  <p className="text-white text-sm">{new Date(decision.decided_at).toLocaleDateString()}</p>
                </div>
                {decision.implemented_at && (
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Implemented</p>
                    <p className="text-white text-sm">{new Date(decision.implemented_at).toLocaleDateString()}</p>
                  </div>
                )}
                {decision.completed_at && (
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Completed</p>
                    <p className="text-white text-sm">{new Date(decision.completed_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            {decision.actual_impact && decision.actual_impact.metrics && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h3 className="font-medium text-green-400 text-sm uppercase tracking-wide mb-3">Results</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">{decision.actual_impact.metrics.primary.label}</p>
                    <p className={`text-2xl font-bold ${decision.actual_impact.metrics.primary.value > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {decision.actual_impact.metrics.primary.value > 0 ? '+' : ''}{decision.actual_impact.metrics.primary.value}{decision.actual_impact.metrics.primary.unit}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">{decision.actual_impact.metrics.secondary.label}</p>
                    <p className="text-2xl font-bold text-white">
                      {decision.actual_impact.metrics.secondary.unit === '$' ? '$' : ''}{decision.actual_impact.metrics.secondary.value}{decision.actual_impact.metrics.secondary.unit !== '$' ? decision.actual_impact.metrics.secondary.unit : ''}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">{decision.actual_impact.metrics.tertiary.label}</p>
                    <p className={`text-2xl font-bold ${decision.actual_impact.metrics.tertiary.value > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {decision.actual_impact.metrics.tertiary.value > 0 ? '+' : ''}{decision.actual_impact.metrics.tertiary.value}{decision.actual_impact.metrics.tertiary.unit}
                    </p>
                  </div>
                </div>
                {decision.actual_impact.cost_impact !== undefined && (
                  <div className="mt-4 pt-4 border-t border-green-500/20 text-center">
                    <p className="text-gray-400 text-xs uppercase mb-1">Cost Impact</p>
                    <p className={`text-xl font-bold ${decision.actual_impact.cost_impact < 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {decision.actual_impact.cost_impact < 0 ? 'Saved' : 'Spent'} ${Math.abs(decision.actual_impact.cost_impact)}
                    </p>
                  </div>
                )}
                {decision.actual_impact.implementation_notes && (
                  <div className="mt-4 pt-4 border-t border-green-500/20">
                    <p className="text-gray-400 text-xs uppercase mb-1">Notes</p>
                    <p className="text-gray-200 text-sm">{decision.actual_impact.implementation_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ARCHIVE MODAL
// ============================================
const ArchiveModal = ({ decision, onClose, onArchive }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleArchive = async () => {
    if (!reason.trim()) return;
    setIsSubmitting(true);
    try {
      await onArchive(decision, reason);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Archive className="w-5 h-5 text-gray-400" />
            Archive Decision
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        
        <div className="mb-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
          <p className="text-yellow-300 text-sm">"{decision.decision_title}"</p>
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Reason for archiving:
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Campaign ended, strategy changed..."
            className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 text-sm"
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 bg-white/10 text-gray-300 py-2 px-4 rounded-lg text-sm font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleArchive}
            disabled={!reason.trim() || isSubmitting}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Archiving...' : 'Archive'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// NEW DECISION FORM
// ============================================
const NewDecisionForm = ({ objectiveTypes, platformOptions, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    campaign_id: '',
    decision_title: '',
    decision_description: '',
    platform: 'facebook',
    custom_platform: '',
    expected_outcome: '',
    objective_type: 'conversion',
    custom_objective: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = () => {
    const hasRequired = formData.campaign_id && formData.decision_title && formData.decision_description && formData.expected_outcome;
    const hasValidObjective = formData.objective_type !== 'custom' || formData.custom_objective;
    const hasValidPlatform = formData.platform !== 'other' || formData.custom_platform;
    return hasRequired && hasValidObjective && hasValidPlatform;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          platform: formData.platform === 'other' ? formData.custom_platform : formData.platform
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create decision');
      }

      onSubmit();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Log New Decision</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Campaign ID *</label>
            <input
              type="text"
              placeholder="e.g., holiday_2024_conversion"
              value={formData.campaign_id}
              onChange={(e) => setFormData({...formData, campaign_id: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Decision Title *</label>
            <input
              type="text"
              placeholder="Brief, clear title of what was decided"
              value={formData.decision_title}
              onChange={(e) => setFormData({...formData, decision_title: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Description *</label>
            <textarea
              placeholder="What's the decision and why?"
              value={formData.decision_description}
              onChange={(e) => setFormData({...formData, decision_description: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Platform</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({...formData, platform: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              >
                {platformOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Objective</label>
              <select
                value={formData.objective_type}
                onChange={(e) => setFormData({...formData, objective_type: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              >
                {objectiveTypes.map(o => (
                  <option key={o.value} value={o.value}>{o.icon} {o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.platform === 'other' && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Specify Platform *</label>
              <input
                type="text"
                placeholder="Platform name"
                value={formData.custom_platform}
                onChange={(e) => setFormData({...formData, custom_platform: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
                disabled={isSubmitting}
              />
            </div>
          )}

          {formData.objective_type === 'custom' && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Custom Objective *</label>
              <input
                type="text"
                placeholder="What's the goal?"
                value={formData.custom_objective}
                onChange={(e) => setFormData({...formData, custom_objective: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
                disabled={isSubmitting}
              />
            </div>
          )}
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Expected Outcome *</label>
            <input
              type="text"
              placeholder="What should happen? Be specific with numbers"
              value={formData.expected_outcome}
              onChange={(e) => setFormData({...formData, expected_outcome: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button 
              onClick={onClose} 
              disabled={isSubmitting}
              className="flex-1 bg-white/10 text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={!isFormValid() || isSubmitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Logging...' : 'Log Decision'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EDIT DECISION FORM
// ============================================
const EditDecisionForm = ({ decision, objectiveTypes, platformOptions, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    campaign_id: decision.campaign_id || '',
    decision_title: decision.decision_title || '',
    decision_description: decision.decision_description || '',
    platform: decision.platform || 'facebook',
    custom_platform: decision.custom_platform || '',
    expected_outcome: decision.expected_outcome || '',
    objective_type: decision.objective_type || 'conversion',
    custom_objective: decision.custom_objective || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = () => {
    const hasRequired = formData.campaign_id && formData.decision_title && formData.decision_description && formData.expected_outcome;
    const hasValidObjective = formData.objective_type !== 'custom' || formData.custom_objective;
    const hasValidPlatform = formData.platform !== 'other' || formData.custom_platform;
    return hasRequired && hasValidObjective && hasValidPlatform;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          platform: formData.platform === 'other' ? formData.custom_platform : formData.platform
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update decision');
      }

      onSubmit();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Edit Decision</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Campaign ID *</label>
            <input
              type="text"
              value={formData.campaign_id}
              onChange={(e) => setFormData({...formData, campaign_id: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Decision Title *</label>
            <input
              type="text"
              value={formData.decision_title}
              onChange={(e) => setFormData({...formData, decision_title: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Description *</label>
            <textarea
              value={formData.decision_description}
              onChange={(e) => setFormData({...formData, decision_description: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Platform</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({...formData, platform: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              >
                {platformOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Objective</label>
              <select
                value={formData.objective_type}
                onChange={(e) => setFormData({...formData, objective_type: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              >
                {objectiveTypes.map(o => (
                  <option key={o.value} value={o.value}>{o.icon} {o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.platform === 'other' && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Specify Platform *</label>
              <input
                type="text"
                value={formData.custom_platform}
                onChange={(e) => setFormData({...formData, custom_platform: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              />
            </div>
          )}

          {formData.objective_type === 'custom' && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Custom Objective *</label>
              <input
                type="text"
                value={formData.custom_objective}
                onChange={(e) => setFormData({...formData, custom_objective: e.target.value})}
                className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                disabled={isSubmitting}
              />
            </div>
          )}
          
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Expected Outcome *</label>
            <input
              type="text"
              value={formData.expected_outcome}
              onChange={(e) => setFormData({...formData, expected_outcome: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button 
              onClick={onClose} 
              disabled={isSubmitting}
              className="flex-1 bg-white/10 text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={!isFormValid() || isSubmitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Updating...' : 'Update Decision'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// IMPLEMENT MODAL
// ============================================
const ImplementModal = ({ decision, objectiveMetrics, onClose, onUpdate }) => {
  const metrics = objectiveMetrics[decision.objective_type] || objectiveMetrics.custom;
  
  const [formData, setFormData] = useState({
    cost_impact: '',
    primary_metric: '',
    secondary_metric: '',
    tertiary_metric: '',
    implementation_notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = () => {
    return formData.cost_impact !== '' && 
           formData.primary_metric !== '' && 
           formData.secondary_metric !== '' && 
           formData.tertiary_metric !== '';
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setError('Please fill in all metric fields');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/implement`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to implement');
      }

      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Mark Implemented</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        
        <div className="mb-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <p className="text-green-300 text-sm">"{decision.decision_title}"</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Cost Impact ($) *</label>
            <input
              type="number"
              placeholder="e.g., -500 for savings"
              value={formData.cost_impact}
              onChange={(e) => setFormData({...formData, cost_impact: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              {metrics.primary.icon} {metrics.primary.label} *
            </label>
            <input
              type="number"
              placeholder={`Value in ${metrics.primary.unit}`}
              value={formData.primary_metric}
              onChange={(e) => setFormData({...formData, primary_metric: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              {metrics.secondary.icon} {metrics.secondary.label} *
            </label>
            <input
              type="number"
              placeholder={`Value in ${metrics.secondary.unit}`}
              value={formData.secondary_metric}
              onChange={(e) => setFormData({...formData, secondary_metric: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              {metrics.tertiary.icon} {metrics.tertiary.label} *
            </label>
            <input
              type="number"
              placeholder={`Value in ${metrics.tertiary.unit}`}
              value={formData.tertiary_metric}
              onChange={(e) => setFormData({...formData, tertiary_metric: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Notes (optional)</label>
            <textarea
              placeholder="Any notes about the implementation..."
              value={formData.implementation_notes}
              onChange={(e) => setFormData({...formData, implementation_notes: e.target.value})}
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400"
              rows={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button 
              onClick={onClose} 
              disabled={isSubmitting}
              className="flex-1 bg-white/10 text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={!isFormValid() || isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Mark Implemented'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPLETE MODAL
// ============================================
const CompleteModal = ({ decision, onClose, onUpdate }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/decisions/${decision.id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to complete');
      }

      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Mark Complete</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>
        
        <div className="mb-4 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
          <p className="text-cyan-300 text-sm">"{decision.decision_title}"</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">
            {error}
          </div>
        )}

        <p className="text-gray-300 text-sm mb-4">
          This will finalize the decision and lock in the results. Ready to close this out?
        </p>

        <div className="flex gap-3">
          <button 
            onClick={onClose} 
            disabled={isSubmitting}
            className="flex-1 bg-white/10 text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            Not yet
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Completing...' : 'Mark Complete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EXPORT PREVIEW MODAL (keeping original, it's fine)
// ============================================
const ExportPreviewModal = ({ decisions, analytics, onClose, onExport, exportingImage, exportTemplateRef }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      completed: { bg: 'bg-emerald-500', gradient: 'from-emerald-600 to-teal-600', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: '✅', barColor: 'from-emerald-400 to-emerald-500' },
      implemented: { bg: 'bg-blue-500', gradient: 'from-blue-600 to-cyan-600', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: '🚀', barColor: 'from-blue-400 to-blue-500' },
      confirmed: { bg: 'bg-cyan-500', gradient: 'from-cyan-600 to-teal-600', light: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: '✓', barColor: 'from-cyan-400 to-cyan-500' },
      pending: { bg: 'bg-amber-500', gradient: 'from-amber-600 to-orange-600', light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: '⏳', barColor: 'from-amber-400 to-amber-500' },
      pending_confirmation: { bg: 'bg-orange-500', gradient: 'from-orange-600 to-red-600', light: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: '🤖', barColor: 'from-orange-400 to-orange-500' },
      overdue: { bg: 'bg-red-500', gradient: 'from-red-600 to-rose-600', light: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: '🔥', barColor: 'from-red-400 to-red-500' }
    };
    return styles[status] || { bg: 'bg-slate-500', gradient: 'from-slate-600 to-gray-600', light: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: '📋', barColor: 'from-slate-400 to-slate-500' };
  };

  const getObjectiveIcon = (objective) => {
    const icons = {
      conversion: '💰',
      brand_awareness: '📢',
      engagement: '❤️',
      traffic: '🚗',
      retention: '🔄',
      email: '📧',
      lead_gen: '🎯',
      custom: '⚡'
    };
    return icons[objective] || '📊';
  };

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gradient-to-r from-purple-600 via-red-600 to-orange-500">
          <div>
            <h2 className="text-2xl font-bold text-white">Export Preview</h2>
            <p className="text-sm text-white/80 mt-1">Preview your beautiful report before exporting</p>
          </div>
          <button 
            onClick={onClose}
            disabled={exportingImage}
            className="text-white/80 hover:text-white text-2xl bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-all disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-900">
          <div className="bg-white rounded-2xl shadow-2xl mx-auto overflow-hidden" style={{ maxWidth: '1000px' }}>
            <div ref={exportTemplateRef} className="bg-white">
              
              {/* Report Header */}
              <div className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 px-10 py-8">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl">👑</span>
                      </div>
                      <div>
                        <h1 className="text-3xl font-bold text-white">
                          {decisions.length === 1 ? 'Decision Report' : `${decisions.length} Decisions`}
                        </h1>
                        <p className="text-purple-300 text-sm font-medium">
                          {currentMonth} • Performance & Accountability
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-purple-300 text-xs uppercase tracking-wider mb-1">Generated</p>
                    <p className="text-white font-semibold">
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-purple-300 text-sm">{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              </div>

              {/* Decisions - Each with its own Executive Summary */}
              <div className="px-10 py-8">
                <div className="space-y-10">
                  {decisions.map((decision, index) => {
                    const statusStyle = getStatusStyle(decision.status);
                    const hasResults = decision.actual_impact && decision.actual_impact.metrics;
                    
                    // Calculate individual decision stats
                    const decisionSavings = decision.actual_impact?.cost_impact < 0 ? Math.abs(decision.actual_impact.cost_impact) : 0;
                    const isExecuted = decision.status === 'completed' || decision.status === 'implemented';
                    const executionRate = isExecuted ? 100 : 0;
                    
                    // Calculate velocity for this decision
                    const getVelocity = () => {
                      const now = new Date();
                      const createdDate = new Date(decision.decided_at);
                      const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
                      
                      // Completed/Implemented - based on how fast they executed
                      if (decision.implemented_at && decision.decided_at) {
                        const days = (new Date(decision.implemented_at) - new Date(decision.decided_at)) / (1000 * 60 * 60 * 24);
                        if (days < 1) return { icon: '⚡', text: 'Lightning' };
                        if (days < 3) return { icon: '🚀', text: 'Fast' };
                        if (days < 7) return { icon: '🏃', text: 'Steady' };
                        return { icon: '🐢', text: 'Slow' };
                      }
                      
                      // Overdue - sarcastic based on how late
                      if (decision.status === 'overdue') {
                        if (daysSinceCreated <= 7) return { icon: '⏰', text: 'Tick Tock' };
                        if (daysSinceCreated <= 14) return { icon: '🐌', text: 'Behind' };
                        if (daysSinceCreated <= 30) return { icon: '🧊', text: 'Frozen' };
                        return { icon: '🕸️', text: 'Dusty' };
                      }
                      
                      // Pending - sarcastic based on how long waiting
                      if (decision.status === 'pending' || decision.status === 'pending_confirmation') {
                        if (daysSinceCreated <= 3) return { icon: '☕', text: 'On Break' };
                        if (daysSinceCreated <= 7) return { icon: '😴', text: 'Snoozing' };
                        if (daysSinceCreated <= 14) return { icon: '🦥', text: 'Chilling' };
                        return { icon: '🐌', text: 'Crawling' };
                      }
                      
                      return { icon: '📊', text: 'N/A' };
                    };
                    
                    const velocity = getVelocity();
                    
                    return (
                      <div key={decision.id} className="border-2 border-slate-200 rounded-2xl overflow-hidden shadow-lg">
                        {/* Decision Header Banner */}
                        <div className={`bg-gradient-to-r ${statusStyle.gradient} px-6 py-4`}>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-white text-lg">
                                {statusStyle.icon}
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-white">{decision.decision_title}</h3>
                                <div className="flex items-center gap-3 mt-1 text-sm text-white/80">
                                  <span className="flex items-center gap-1">
                                    <span>{getObjectiveIcon(decision.objective_type)}</span>
                                    <span>{decision.objective_type === 'custom' && decision.custom_objective ? decision.custom_objective : decision.objective_type}</span>
                                  </span>
                                  <span>•</span>
                                  <span>{decision.displayPlatform || decision.platform || 'Not specified'}</span>
                                  <span>•</span>
                                  <span>{formatDate(decision.decided_at)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="bg-white/20 px-4 py-1.5 rounded-full">
                              <span className="text-sm font-bold text-white uppercase tracking-wide">
                                {decision.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Individual Executive Summary */}
                        <div className="px-6 py-6 bg-gradient-to-b from-slate-50 to-white">
                          <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <span className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white text-xs">📊</span>
                            Executive Summary
                          </h4>
                          
                          {/* Stats Grid */}
                          <div className="grid grid-cols-4 gap-3 mb-4">
                            <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 text-white shadow-md">
                              <p className="text-violet-200 text-xs font-medium uppercase tracking-wider mb-1">Decision</p>
                              <p className="text-2xl font-bold">1</p>
                              <p className="text-violet-200 text-xs mt-1">Logged</p>
                            </div>
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-4 text-white shadow-md">
                              <p className="text-blue-200 text-xs font-medium uppercase tracking-wider mb-1">Execution</p>
                              <p className="text-2xl font-bold">{executionRate}%</p>
                              <p className="text-blue-200 text-xs mt-1">{isExecuted ? 'Executed' : 'Pending'}</p>
                            </div>
                            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-4 text-white shadow-md">
                              <p className="text-emerald-200 text-xs font-medium uppercase tracking-wider mb-1">Velocity</p>
                              <p className="text-2xl font-bold">{velocity.icon} {velocity.text}</p>
                              <p className="text-emerald-200 text-xs mt-1">Speed</p>
                            </div>
                            
                            {/* Dynamic 4th card */}
                            {decisionSavings > 0 ? (
                              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-4 text-white shadow-md">
                                <p className="text-amber-200 text-xs font-medium uppercase tracking-wider mb-1">Savings</p>
                                <p className="text-2xl font-bold">${decisionSavings.toLocaleString()}</p>
                                <p className="text-amber-200 text-xs mt-1">Cost reduced</p>
                              </div>
                            ) : (
                              <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl p-4 text-white shadow-md">
                                <p className="text-rose-200 text-xs font-medium uppercase tracking-wider mb-1">Status</p>
                                <p className="text-2xl font-bold">{isExecuted ? '✓' : '...'}</p>
                                <p className="text-rose-200 text-xs mt-1">{isExecuted ? 'Accomplished' : 'In Progress'}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Status Bar */}
                          <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full bg-gradient-to-r ${statusStyle.barColor}`}
                                  style={{ width: `${executionRate}%` }}
                                />
                              </div>
                            </div>
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>Progress</span>
                              <span className="font-medium">{executionRate}% Complete</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Decision Details */}
                        <div className="px-6 py-5 bg-white border-t border-slate-100">
                          <div className="grid grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                              {decision.decision_description && (
                                <div>
                                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Description</p>
                                  <p className="text-sm text-slate-700 leading-relaxed">{decision.decision_description}</p>
                                </div>
                              )}
                              
                              <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Expected Outcome</p>
                                <p className="text-sm text-slate-700 font-medium">{decision.expected_outcome || 'Not specified'}</p>
                              </div>

                              {decision.accountability_score && (
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl px-4 py-2">
                                  <span className="text-amber-500 text-lg">⭐</span>
                                  <div>
                                    <p className="text-xs text-amber-600 font-medium">Accountability Score</p>
                                    <p className="text-xl font-bold text-amber-700">{decision.accountability_score}/10</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                              {/* Timeline */}
                              <div className="bg-slate-50 rounded-xl p-4">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Timeline</p>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Created</span>
                                    <span className="font-medium text-slate-700">{formatDate(decision.decided_at)}</span>
                                  </div>
                                  {decision.implemented_at && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Implemented</span>
                                      <span className="font-medium text-slate-700">{formatDate(decision.implemented_at)}</span>
                                    </div>
                                  )}
                                  {decision.completed_at && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Completed</span>
                                      <span className="font-medium text-slate-700">{formatDate(decision.completed_at)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Owner */}
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                  {(decision.decided_by || 'U')[0].toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-xs text-slate-400">Owner</p>
                                  <p className="text-sm font-medium text-slate-700">{decision.decided_by || 'Unknown'}</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Results Section */}
                          {hasResults && (
                            <div className="mt-6 pt-6 border-t border-slate-100">
                              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <span className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-teal-500 rounded flex items-center justify-center text-white text-xs">📈</span>
                                Results & Impact
                              </p>
                              <div className="grid grid-cols-4 gap-4">
                                {decision.actual_impact.metrics.primary && (
                                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 text-center border border-slate-200">
                                    <p className="text-xs text-slate-500 mb-1">{decision.actual_impact.metrics.primary.label}</p>
                                    <p className={`text-2xl font-bold ${decision.actual_impact.metrics.primary.value > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {decision.actual_impact.metrics.primary.value > 0 ? '+' : ''}{decision.actual_impact.metrics.primary.value}{decision.actual_impact.metrics.primary.unit}
                                    </p>
                                  </div>
                                )}
                                {decision.actual_impact.metrics.secondary && (
                                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 text-center border border-slate-200">
                                    <p className="text-xs text-slate-500 mb-1">{decision.actual_impact.metrics.secondary.label}</p>
                                    <p className="text-2xl font-bold text-slate-700">
                                      {decision.actual_impact.metrics.secondary.unit === '$' ? '$' : ''}{decision.actual_impact.metrics.secondary.value}{decision.actual_impact.metrics.secondary.unit !== '$' ? decision.actual_impact.metrics.secondary.unit : ''}
                                    </p>
                                  </div>
                                )}
                                {decision.actual_impact.metrics.tertiary && (
                                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 text-center border border-slate-200">
                                    <p className="text-xs text-slate-500 mb-1">{decision.actual_impact.metrics.tertiary.label}</p>
                                    <p className={`text-2xl font-bold ${decision.actual_impact.metrics.tertiary.value > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {decision.actual_impact.metrics.tertiary.value > 0 ? '+' : ''}{decision.actual_impact.metrics.tertiary.value}{decision.actual_impact.metrics.tertiary.unit}
                                    </p>
                                  </div>
                                )}
                                {decision.actual_impact.cost_impact !== undefined && (
                                  <div className={`rounded-xl p-4 text-center border ${decision.actual_impact.cost_impact < 0 ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-200'}`}>
                                    <p className="text-xs text-slate-500 mb-1">Cost Impact</p>
                                    <p className={`text-2xl font-bold ${decision.actual_impact.cost_impact < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {decision.actual_impact.cost_impact < 0 ? '-' : '+'}${Math.abs(decision.actual_impact.cost_impact)}
                                    </p>
                                    <p className={`text-xs ${decision.actual_impact.cost_impact < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {decision.actual_impact.cost_impact < 0 ? 'Saved' : 'Spent'}
                                    </p>
                                  </div>
                                )}
                              </div>
                              
                              {decision.actual_impact.implementation_notes && (
                                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Implementation Notes</p>
                                  <p className="text-sm text-blue-800">{decision.actual_impact.implementation_notes}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 px-10 py-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white text-xl">👑</span>
                    </div>
                    <div>
                      <p className="text-white font-bold">WhyDidItDie</p>
                      <p className="text-purple-300 text-xs">Decision Tracker</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-purple-300 text-xs">Marketing accountability made clear</p>
                    <p className="text-white text-sm font-medium mt-1">whydiditdie.com</p>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-between items-center bg-gray-800">
          <div className="text-sm text-gray-400">
            <p className="font-medium text-white">📸 {decisions.length} decision{decisions.length !== 1 ? 's' : ''} ready to export</p>
            <p className="text-xs mt-1">High-resolution PNG • Perfect for reports & presentations</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={exportingImage}
              className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onExport}
              disabled={exportingImage}
              className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-red-500/25"
            >
              {exportingImage ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export as Image
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionTracker;